import { RouteRecordRaw } from 'vue-router';
import { isViewportBelow } from '@/theme/breakpoints';

export const projectRoutes: RouteRecordRaw[] = [
  {
    path: '/projects',
    name: 'ProjectsRoot',
    component: () => import('@/pages/project/ProjectsRoot.vue'),
    children: [
      {
        path: '',
        name: 'Projects',
        component: () => import('@/pages/project/ProjectListPage.vue'),
      },
      {
        path: ':projectId',
        name: 'ProjectRoot',
        component: () => import('@/pages/project/ProjectRoot.vue'),
        children: [
          {
            path: '',
            name: 'ProjectPage',
            component: () => import('@/pages/common/RemapPage.vue'),
            meta: { hideTopNavigation: true },
            beforeEnter: (to, from, next) => {
              if (isViewportBelow('map')) {
                next({ name: 'ProjectListView', params: to.params });
              } else {
                next();
              }
            },
          },
          {
            path: 'overview',
            name: 'ProjectOverview',
            component: () => import('@/pages/project/ProjectOverviewPage.vue'),
          },
          {
            path: 'list',
            name: 'ProjectListView',
            component: () => import('@/pages/project/ProjectListViewPage.vue'),
          },
          {
            path: 'kanban',
            name: 'ProjectKanbanView',
            component: () =>
              import('@/pages/project/ProjectKanbanViewPage.vue'),
          },

          {
            path: 'history',
            name: 'ProjectHistory',
            component: () => import('@/pages/project/ProjectHistoryPage.vue'),
          },

          {
            path: 'meetings',
            name: 'ProjectMeetings',
            component: () => import('@/pages/project/ProjectMeetingsPage.vue'),
          },

          {
            path: 'activities',
            name: 'ProjectActivities',
            component: () =>
              import('@/pages/project/activity/ProjectActivitiesPage.vue'),
          },

          {
            path: 'milestones',
            name: 'ProjectMilestones',
            component: () =>
              import('@/pages/project/milestone/ProjectMilestonesPage.vue'),
          },

          {
            path: 'analytics/volatility',
            name: 'ProjectVolatilityAnalytics',
            component: () =>
              import('@/pages/project/ProjectVolatilityAnalyticsPage.vue'),
          },

          {
            path: 'search-results',
            name: 'ProjectSearchResults',
            component: () =>
              import('@/pages/project/SearchResultsDetailPage.vue'),
          },

          {
            path: 'settings',
            name: 'ProjectSettings',
            component: () => import('@/pages/project/ProjectSettingsPage.vue'),
            redirect: (from) => ({
              name: 'ProjectSettingsFunctions',
              params: from.params,
            }),
            children: [
              {
                path: 'functions',
                name: 'ProjectSettingsFunctions',
                component: () =>
                  import('@/pages/project/ProjectSettingsFunctionsPage.vue'),
              },
              {
                path: 'documents',
                name: 'ProjectSettingsDocuments',
                component: () =>
                  import('@/pages/project/ProjectSettingsDocumentsPage.vue'),
              },
              {
                path: 'members',
                name: 'ProjectSettingsMembers',
                component: () =>
                  import('@/pages/project/ProjectSettingsMembersPage.vue'),
              },
              {
                path: 'links-and-tools',
                name: 'ProjectLinksAndTools',
                component: () =>
                  import(
                    '@/pages/project/ProjectSettingsLinksAndToolsPage.vue'
                  ),
              },
              {
                path: 'checklist-rows',
                name: 'ProjectSettingsChecklistRows',
                component: () =>
                  import(
                    '@/pages/project/ProjectSettingsChecklistRowsPage.vue'
                  ),
              },
            ],
          },

          {
            path: 'activities/:activityId',
            name: 'ProjectActivity',
            component: () =>
              import('@/pages/project/activity/ProjectActivityRoot.vue'),
            children: [
              {
                path: '',
                name: 'ProjectActivityDetail',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityDetailPage.vue'
                  ),
              },
              {
                path: 'completion-criteria',
                name: 'ProjectCriteriaRoot',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityCriteriaRoot.vue'
                  ),
                children: [
                  {
                    path: '',
                    name: 'ProjectActivityCompletionCriteria',
                    component: () =>
                      import(
                        '@/pages/project/activity/ProjectActivityCompletionCriteria.vue'
                      ),
                  },
                  {
                    path: ':completionCriterionId',
                    name: 'ProjectCriterion',
                    // prettier-ignore
                    component: () => import('@/pages/project/activity/ProjectActivityCriterionPage.vue'),
                  },
                ],
              },
              {
                path: 'checklist',
                name: 'ProjectActivityChecklist',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityChecklistPage.vue'
                  ),
              },
              {
                path: 'actions',
                name: 'ProjectActivityActionsRoot',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityActionsRoot.vue'
                  ),
                children: [
                  {
                    path: '',
                    name: 'ProjectActivityActions',
                    component: () =>
                      import(
                        '@/pages/project/activity/ProjectActivityActionsPage.vue'
                      ),
                  },
                  {
                    path: ':actionId',
                    name: 'ProjectActivityAction',
                    // prettier-ignore
                    component: () => import('@/pages/project/activity/ProjectActivityActionPage.vue'),
                  },
                ],
              },
              {
                path: 'comments',
                name: 'ProjectActivityCommentsPage',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityCommentsPage.vue'
                  ),
              },
              {
                path: 'watchers',
                name: 'ProjectActivityWatchersPage',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityWatchersPage.vue'
                  ),
              },
              {
                path: 'documents',
                name: 'ProjectActivityDocuments',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityDocumentsPage.vue'
                  ),
              },
              {
                path: 'approvals',
                name: 'ProjectActivityApprovals',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityApprovalsPage.vue'
                  ),
              },
              {
                path: 'predecessors',
                name: 'ProjectActivityPredecessors',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityPredecessorsPage.vue'
                  ),
              },
              {
                path: 'successors',
                name: 'ProjectActivitySuccessors',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivitySuccessorsPage.vue'
                  ),
              },
              {
                path: 'links-and-tools',
                name: 'ProjectActivityLinksAndTools',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityLinksToolsPage.vue'
                  ),
              },
              {
                path: 'history',
                name: 'ProjectActivityHistory',
                component: () =>
                  import('@/pages/project/ProjectHistoryPage.vue'),
              },
              {
                path: 'references',
                name: 'ProjectActivityReferences',
                component: () =>
                  import(
                    '@/pages/project/activity/ProjectActivityReferencesPage.vue'
                  ),
              },
            ],
          },
          {
            path: 'milestones/:milestoneId',
            name: 'ProjectMilestoneRootPage',
            component: () =>
              import('@/pages/project/milestone/ProjectMilestoneRootPage.vue'),
            children: [
              {
                path: '',
                name: 'ProjectMilestoneDetailPage',
                component: () =>
                  import(
                    '@/pages/project/milestone/ProjectMilestoneDetailPage.vue'
                  ),
              },
              {
                path: 'approvals',
                name: 'ProjectMilestoneApprovalsPage',
                component: () =>
                  import(
                    '@/pages/project/milestone/ProjectMilestoneApprovalsPage.vue'
                  ),
              },
              {
                path: 'activities/:required(required|optional)',
                name: 'ProjectMilestoneActivitiesPage',
                component: () =>
                  import(
                    '@/pages/project/milestone/ProjectMilestoneActivitiesPage.vue'
                  ),
              },
              {
                path: 'comments',
                name: 'ProjectMilestoneCommentsPage',
                component: () =>
                  import(
                    '@/pages/project/milestone/ProjectMilestoneCommentsPage.vue'
                  ),
              },
              {
                path: 'references',
                name: 'ProjectMilestoneReferences',
                component: () =>
                  import(
                    '@/pages/project/milestone/ProjectMilestoneReferencesPage.vue'
                  ),
              },
            ],
          },
        ],
      },
    ],
  },
];
